import React from 'react';

import { useInlineOtpVerification } from '../../hooks/useInlineOtpVerification';
import { useMultiStepNavigation } from '../../hooks/useMultiStepNavigation';
import { useOtpFieldRef } from '../../hooks/useOtpFieldRef';
import { useStepValidation } from '../../hooks/useStepValidation';
import { MultiStepRegistrationFormProps } from '../../types/MultiStepRegistrationFormTypes';
import { MultiStepFormFooter } from '../MultiStepFormFooter';
import { MultiStepFormHeader } from '../MultiStepFormHeader';
import Multistep, { useMultistepApi } from '../ExtendedMultiStepField';
import { FormRenderer } from '../FormRenderer';

import { FormFieldSet } from './styledComponents';

export const MultiStepRegistrationForm: React.FC<
  MultiStepRegistrationFormProps
> = ({
  eachFormStep,
  formApi,
  formValues,
  errors,
  touched,
  publicFormMachineSend,
  publicFormMachineState,
  validationSchemaObject,
  customClassNames,
  renderCustomSuccessPage,
  customHeader,
  getNumberedProgressBarTitles,
  t,
  handleFormReset = () => { },
  designSystemType,
  onValidatedBeforeStepActions
}) => {
    const {
      stepId,
      nextStepDetails,
      stepFields,
      stepType: currentStepType
    } = eachFormStep;

    const {
      btn_id: nextBtnId,
      btn_text: nextBtnText,
      btn_actions: nextBtnActions,
      step_id: nextStepId = ''
    } = nextStepDetails ?? {};

    const { btn_actions: prevBtnActions, step_id: prevStepId } =
      eachFormStep.previousStepDetails ?? {};

    const { next, back } = useMultistepApi();

    //NOTE: isLoggedIn & formSubmissionDetails is used to check the eval condition for nextStepId which is defined in config
    const isLoggedIn = publicFormMachineState.context.isLoggedIn;
    const formSubmissionDetails = (
      publicFormMachineState.context as { formSubmissionDetails?: unknown }
    ).formSubmissionDetails;

    const {
      onClickBackButton,
      handleValidFormSubmission,
      tryAdvanceViaGoToNextSteps
    } = useMultiStepNavigation({
      formApi,
      formValues,
      publicFormMachineState,
      publicFormMachineSend,
      currentStepType,
      stepId,
      nextBtnActions,
      prevBtnActions,
      next,
      back,
      onValidatedBeforeStepActions
    });

    const {
      inlineOtpVerificationError,
      showInlineOtpVerificationError,
      clearInlineOtpVerificationError,
      isInlineOtpVerified,
      isInlineOtpVerificationPending
    } = useInlineOtpVerification({
      formApi,
      stepFields,
      formValues,
      publicFormMachineState,
      designSystemType
    });

    const { validateAndProceed } = useStepValidation({
      formApi,
      stepFields,
      designSystemType,
      isInlineOtpVerified,
      isInlineOtpVerificationPending,
      clearInlineOtpVerificationError,
      showInlineOtpVerificationError,
      onValidSubmission: handleValidFormSubmission
    });

    const onClickNextBtn = (): void => {
      if (!tryAdvanceViaGoToNextSteps()) {
        validateAndProceed();
      }
    };

    const otpFieldRef = useOtpFieldRef(publicFormMachineState);

    const renderFormFields = (): React.ReactElement | null => {
      return stepFields.length > 0 ? (
        <FormRenderer
          informedFieldsInfo={{
            formValues,
            errors,
            touched,
            formApi,
            stepFields
          }}
          registrationFormState={publicFormMachineState}
          registrationFormSend={publicFormMachineSend}
          validationSchemaObject={validationSchemaObject}
          customClassNames={customClassNames}
          handleFormReset={handleFormReset}
          designSystemType={designSystemType}
          inlineOtpVerificationError={inlineOtpVerificationError}
          onClearInlineOtpVerificationError={clearInlineOtpVerificationError}
        />
      ) : null;
    };

    return (
      <Multistep.Step step={stepId} next={eval(nextStepId)} previous={prevStepId}>
        <FormFieldSet>
          <MultiStepFormHeader
            eachFormStep={eachFormStep}
            formValues={formValues}
            customClassNames={customClassNames}
            customHeader={customHeader}
            getNumberedProgressBarTitles={getNumberedProgressBarTitles}
            renderCustomSuccessPage={renderCustomSuccessPage}
            publicFormMachineState={publicFormMachineState}
            publicFormMachineSend={publicFormMachineSend}
            onBack={onClickBackButton}
            t={t}
          />
          {renderFormFields()}
          <MultiStepFormFooter
            eachFormStep={eachFormStep}
            formApi={formApi}
            formValues={formValues}
            customClassNames={customClassNames}
            publicFormMachineState={publicFormMachineState}
            publicFormMachineSend={publicFormMachineSend}
            designSystemType={designSystemType}
            stepId={stepId}
            nextBtnId={nextBtnId}
            nextBtnText={nextBtnText}
            nextBtnActions={nextBtnActions}
            onClickNextBtn={onClickNextBtn}
            otpFieldRef={otpFieldRef}
            t={t}
          />
        </FormFieldSet>
      </Multistep.Step>
    );
  };